namespace $ {

	export function $shm_codex_app_video_static_uri(
		source: string,
		location: Pick< Location, 'hostname' | 'origin' | 'port' >,
	) {
		const normalized = source.replace( /\\/g, '/' )
		const marker = normalized.toLowerCase().indexOf( '/shm/' )
		if( marker < 0 || location.port !== '9080' ) return ''
		if( ![ 'localhost', '127.0.0.1' ].includes( location.hostname ) ) return ''
		return `${ location.origin }/${ normalized.slice( marker + 1 ) }`
	}

}

namespace $.$$ {

	export class $shm_codex_app extends $.$shm_codex_app {

		@ $mol_mem
		codex_models() {
			return $mol_wire_sync( $shm_codex_api ).models( this.server_url() ).data
		}

		@ $mol_mem
		codex_limits_live( next?: $shm_codex_api_limits | null ) {
			return next === undefined ? null : next
		}

		@ $mol_mem
		codex_limits() {
			return this.codex_limits_live()
				?? $mol_wire_sync( $shm_codex_api ).limits( this.server_url() )
		}

		agent_model_options() {
			return this.codex_models().map( model => model.id )
		}

		agent_model_dict() {
			const dict = {} as Record< string, string >
			for( const model of this.codex_models() ) dict[ model.id ] = model.displayName || model.id
			return dict
		}

		agent_defaults_namespace() {
			return this.agent_storage_namespace()
		}

		codex_model_default_key() {
			return `shm_${ this.agent_defaults_namespace() }_session_model_default`
		}

		codex_fast_default_key() {
			return `shm_${ this.agent_defaults_namespace() }_session_fast_default`
		}

		codex_model_fast_supported( id: string ) {
			return Boolean( this.codex_models()
				.find( model => model.id === id )
				?.serviceTiers?.some( tier => tier.id === 'priority' ) )
		}

		override agent_model_default_id( next?: string ) {
			const key = this.codex_model_default_key()
			if( next !== undefined ) {
				this.$.$mol_state_local.value( key, next )
				if( !this.codex_model_fast_supported( next ) ) this.agent_fast_default_enabled( false )
				return next
			}
			const stored = this.$.$mol_state_local.value< string >( key ) ?? ''
			if( stored && this.agent_model_options().includes( stored ) ) return stored
			if( this.agent_model_options().includes( 'gpt-6.1-sol' ) ) return 'gpt-6.1-sol'
			return this.codex_models().find( model => model.isDefault )?.id || this.agent_model_options()[ 0 ] || ''
		}

		agent_fast_default_supported() {
			return this.codex_model_fast_supported( this.agent_model_default_id() )
		}

		override agent_fast_default_enabled( next?: boolean ) {
			const key = this.codex_fast_default_key()
			if( next !== undefined ) {
				const enabled = next && this.agent_fast_default_supported()
				if( enabled ) this.$.$mol_state_local.value(
					this.codex_model_default_key(),
					this.agent_model_default_id(),
				)
				this.$.$mol_state_local.value( key, enabled )
				return enabled
			}
			return this.agent_fast_default_supported()
				&& Boolean( this.$.$mol_state_local.value( key ) )
		}

		agent_session_defaults() {
			const model = this.agent_model_default_id()
			return {
				...( model ? { model } : {} ),
				serviceTier: this.agent_fast_default_enabled() ? 'priority' : null,
			}
		}

		override agent_model_id( next?: string ) {
			const id = this.session_id()
			if( !id ) return this.agent_model_default_id( next )
			if( next !== undefined ) {
				const fast_supported = Boolean( this.codex_models()
					.find( model => model.id === next )
					?.serviceTiers?.some( tier => tier.id === 'priority' ) )
				const service_tier = fast_supported && this.agent_fast_enabled() ? 'priority' : null
				const result = $mol_wire_sync( $shm_codex_api ).model(
					this.server_url(), id, next, service_tier,
				)
				this.session_upsert( result.thread )
				return next
			}
			const session = this.session_by_id( id )
			if( session?.model && this.agent_model_options().includes( session.model ) ) return session.model
			const thread = this.$.$mol_error_fence(
				()=> this.thread_data()?.thread,
				()=> null,
				()=> null,
			)
			const selected = thread?.model || this.session_by_id( id )?.model
			if( selected && this.agent_model_options().includes( selected ) ) return selected
			return this.codex_models().find( model => model.isDefault )?.id || this.agent_model_options()[ 0 ] || ''
		}

		agent_model_label() {
			const fallback = this.session_by_id( this.session_id() )
			const thread = this.$.$mol_error_fence(
				()=> this.thread_data()?.thread || fallback,
				()=> fallback,
				()=> fallback,
			)
			return fallback?.model || thread?.model || fallback?.modelProvider || thread?.modelProvider || 'Codex'
		}

		agent_fast_supported() {
			const model = this.codex_models().find( model => model.id === this.agent_model_id() )
			return Boolean( model?.serviceTiers?.some( tier => tier.id === 'priority' ) )
		}

		override agent_fast_enabled( next?: boolean ) {
			const id = this.session_id()
			if( !id ) return this.agent_fast_default_enabled( next )
			if( next !== undefined ) {
				if( !id || ( next && !this.agent_fast_supported() ) ) return false
				const result = $mol_wire_sync( $shm_codex_api ).service_tier(
					this.server_url(), id, next ? 'priority' : null,
				)
				this.session_upsert( result.thread )
				return next
			}
			const session = this.session_by_id( id )
			if( session?.serviceTier !== undefined ) return session.serviceTier === 'priority'
			const thread = this.$.$mol_error_fence(
				()=> this.thread_data()?.thread,
				()=> null,
				()=> null,
			)
			return thread?.serviceTier === 'priority'
		}

		agent_limits() {
			return this.$.$mol_error_fence(
				()=> {
					const limits = this.codex_limits()
					const buckets = limits.rateLimitsByLimitId && Object.keys( limits.rateLimitsByLimitId ).length
						? Object.entries( limits.rateLimitsByLimitId )
						: [[ limits.rateLimits.limitId || 'codex', limits.rateLimits ]] as const
					const multiple = buckets.length > 1
					return buckets.flatMap( ( [ id, bucket ] )=> {
						const prefix = multiple ? bucket.limitName || bucket.limitId || id : ''
						return ([ 'primary', 'secondary' ] as const).flatMap( kind => {
							const window = bucket[ kind ]
							if( !window ) return []
							const duration = this.limit_duration_label( window.windowDurationMins )
							const title = [ prefix, duration ].filter( Boolean ).join( ' · ' ) || 'Лимит'
							const remaining = Math.max( 0, Math.min( 100, 100 - window.usedPercent ) )
							const reset = window.resetsAt
								? new Date( window.resetsAt * 1000 ).toLocaleString( 'ru-RU', {
									day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
								} )
								: ''
							return [{
								id: `${ id }:${ kind }`,
								title,
								value: `Осталось ${ remaining.toLocaleString( 'ru-RU', { maximumFractionDigits: 1 } ) }%${ reset ? ` · до ${ reset }` : '' }`,
							}]
						} )
					} )
				},
				()=> [{ id: 'error', title: 'Статус', value: 'Не удалось загрузить' }],
				()=> [{ id: 'loading', title: 'Статус', value: 'Загрузка…' }],
			)
		}

		limit_duration_label( minutes: number | null ) {
			if( !minutes ) return ''
			if( minutes % ( 24 * 60 ) === 0 ) return `${ minutes / ( 24 * 60 ) } дн.`
			if( minutes % 60 === 0 ) return `${ minutes / 60 } ч`
			return `${ minutes } мин.`
		}

		agent_event_handle( event: any ) {
			if( event.method === 'thread/settings/updated' && event.params?.threadId ) {
				const thread = String( event.params.threadId )
				const settings = event.params.threadSettings
				const current = this.session_by_id( thread )
				if( current && settings ) this.session_upsert({
					... current,
					...( settings.model ? { model: settings.model } : {} ),
					...( 'serviceTier' in settings ? { serviceTier: settings.serviceTier } : {} ),
				})
			}
			if( event.method !== 'account/rateLimits/updated' || !event.params?.rateLimits ) return
			const rateLimits = event.params.rateLimits as $shm_codex_api_rate_limit
			const current = this.codex_limits_live()
			const limitId = rateLimits.limitId || current?.rateLimits.limitId || 'codex'
			this.codex_limits_live({
				rateLimits: { ...( current?.rateLimits ?? {} as any ), ...rateLimits },
				rateLimitsByLimitId: {
					...( current?.rateLimitsByLimitId ?? {} ),
					[ limitId ]: {
						...( current?.rateLimitsByLimitId?.[ limitId ] ?? {} as any ),
						...rateLimits,
					},
				},
			} )
		}

		agent_storage_namespace() { return 'codex' }
		agent_name() { return 'Codex' }
		sessions_title() { return 'Codex' }

		override message_image_uri_resolve( uri: string ) {
			if( !uri || /^(?:data:|blob:|https?:)/i.test( uri ) ) return uri
			return $mol_wire_sync( $shm_codex_api ).file_data( this.server_url(), this.session_id(), uri )
		}

		override message_video_uri_resolve( uri: string ) {
			if( !uri || /^(?:data:|blob:|https?:)/i.test( uri ) ) return uri
			const source = $shm_codex_api.file_test_artifact_fallback( uri ) || uri
			const location = this.$.$mol_dom_context.location
			const local = $shm_codex_app_video_static_uri( source, location )
			if( local ) return local
			return $mol_wire_sync( $shm_codex_api ).file_data( this.server_url(), this.session_id(), source )
		}

		agent_file_text( path: string ) {
			return $mol_wire_sync( $shm_codex_api ).file_text( this.server_url(), this.session_id(), path )
		}

		agent_file_data( path: string ) {
			return $mol_wire_sync( $shm_codex_api ).file_data( this.server_url(), this.session_id(), path )
		}

		server_url( next?: string ) {
			const host = this.$.$mol_dom_context.location.host
			if( host.endsWith( '.158-160-195-195.ip.hyoo.ru' ) ) return 'https://codex-dev.158-160-195-195.ip.hyoo.ru'
			if( host === 'mam.138-124-242-95.ip.hyoo.ru' ) return `${ this.$.$mol_dom_context.location.origin }/codex-api`
			return 'http://127.0.0.1:4870'
		}

		agent_sessions_load( refresh = 0 ) {
			return $mol_wire_sync( $shm_codex_api ).sessions( this.server_url(), refresh )
		}

		@ $mol_mem_key
		agent_thread_request( key: string ) {
			const [ base, id, refresh ] = JSON.parse( key ) as [ string, string, number ]
			return $mol_wire_sync( $shm_codex_api ).thread( base, id, refresh ) as $shm_agent_thread_data
		}

		agent_thread_load( id: string, refresh: number ) {
			return this.agent_thread_request( JSON.stringify([ this.server_url(), id, refresh ]) )
		}

		agent_history_catalog_load( id: string, before: string, refresh: number ) {
			return $mol_wire_sync( $shm_codex_api ).history_catalog( this.server_url(), id, before, 40, refresh )
		}

		agent_history_turn_load( id: string, turn: string, refresh: number ) {
			return $mol_wire_sync( $shm_codex_api ).history_turn( this.server_url(), id, turn, refresh )
		}

		agent_session_create(
			title: string,
			cwd: string,
			instructions: string,
			settings: { model?: string, serviceTier?: string | null } = {},
		) {
			return $mol_wire_sync( $shm_codex_api ).create_session(
				this.server_url(), title, cwd, instructions, settings,
			)
		}

		agent_session_create_async(
			title: string,
			cwd: string,
			instructions: string,
			settings: { model?: string, serviceTier?: string | null } = {},
		) {
			return $shm_codex_api.create_session( this.server_url(), title, cwd, instructions, settings )
		}

		agent_session_rename( id: string, name: string ) {
			return $mol_wire_sync( $shm_codex_api ).rename( this.server_url(), id, name )
		}

		agent_session_tags( id: string, tags: readonly string[] ) {
			return $shm_codex_api.tags( this.server_url(), id, tags )
		}

		agent_session_remove( id: string ) {
			return $shm_codex_api.remove( this.server_url(), id )
		}

		agent_session_fork( id: string ) {
			return $mol_wire_sync( $shm_codex_api ).fork( this.server_url(), id )
		}

		agent_message_send(
			id: string,
			text: string,
			attachments: readonly $shm_agent_attachment[],
			mode: 'start' | 'queue' | 'steer',
			client_message_id: string,
		) {
			return $mol_wire_sync( $shm_codex_api ).send_with_images(
				this.server_url(), id, text, attachments, mode, client_message_id,
			)
		}

		agent_message_force( thread: string, queue: string ) {
			return $shm_codex_api.queue_force( this.server_url(), thread, queue )
		}

		agent_message_queue_edit( thread: string, queue: string, text: string ) {
			return $shm_codex_api.queue_edit( this.server_url(), thread, queue, text )
		}

		agent_message_queue_remove( thread: string, queue: string ) {
			return $shm_codex_api.queue_remove( this.server_url(), thread, queue )
		}

		agent_session_stop( id: string ) {
			return $mol_wire_sync( $shm_codex_api ).stop( this.server_url(), id )
		}

		agent_goal_supported() { return true }

		agent_goal_load( id: string, refresh: number ) {
			return $mol_wire_sync( $shm_codex_api ).goal( this.server_url(), id, refresh ).goal
		}

		agent_goal_set( id: string, objective: string ) {
			return $mol_wire_sync( $shm_codex_api ).goal_set( this.server_url(), id, objective )
		}

		agent_goal_clear( id: string ) {
			return $mol_wire_sync( $shm_codex_api ).goal_clear( this.server_url(), id )
		}

		agent_goal_pause( id: string ) {
			return $mol_wire_sync( $shm_codex_api ).goal_pause( this.server_url(), id )
		}

		@ $mol_mem
		events() {
			return this.$.$shm_codex_api_events.make({
				url: ()=> this.server_url(),
				thread: $mol_const( '' ),
				prefixes: ()=> this.events_prefixes(),
				notify: $mol_const( event => ( this as any ).event_handle( event ) ),
			})
		}

		events_prefixes() {
			return ''
		}

		@ $mol_mem
		file_events() {
			const thread = this.session_id()
			const external = thread && this.$.$mol_error_fence(
				()=> !! ( this as any ).session_external( thread ),
				()=> false,
				()=> false,
			)
			if( !thread || !external ) return { native: ()=> null }
			return this.$.$shm_codex_api_events.make({
				url: ()=> this.server_url(),
				thread: $mol_const( thread ),
				file_only: $mol_const( true ),
				notify: $mol_const( event => ( this as any ).event_handle( event ) ),
			})
		}

	}

}
